import type { WorldConfig } from '../../config/world.config';
import type { GreyboxMaterialId, GreyboxPart } from '../AssetTypes';
import { GreyboxMaterialId as M } from '../AssetTypes';

type Triple = readonly [number, number, number];

/** Box whose bottom sits at `y` (more natural for ground-standing parts). */
export function box(size: Triple, x: number, y: number, z: number, material: GreyboxMaterialId, rotation?: Triple): GreyboxPart {
  return { shape: 'box', size, position: [x, y + size[1] / 2, z], material, ...(rotation ? { rotation } : {}) };
}

/** Upright cylinder whose bottom sits at `y`. */
export function cylinder(diameter: number, height: number, x: number, y: number, z: number, material: GreyboxMaterialId): GreyboxPart {
  return { shape: 'cylinder', size: [diameter, height, diameter], position: [x, y + height / 2, z], material };
}

/** Cylinder lying along Z, centered at the given position. */
export function pipeAlongZ(diameter: number, length: number, x: number, y: number, z: number, material: GreyboxMaterialId): GreyboxPart {
  return { shape: 'cylinder', size: [diameter, length, diameter], position: [x, y, z], rotation: [90, 0, 0], material };
}

export function sphere(diameter: number, x: number, y: number, z: number, material: GreyboxMaterialId): GreyboxPart {
  return { shape: 'sphere', size: [diameter, diameter, diameter], position: [x, y, z], material };
}

/** Greybox of a straight track module (see SEGMENT_STANDARD.md for the cross-section). */
export function straightSegmentGreybox(world: WorldConfig, length: number): GreyboxPart[] {
  const halfTrack = world.trackWidth / 2;
  const curbWidth = 0.3;
  const sideWidth = 12;
  const fenceX = 4.2;
  const fenceHeight = 2;
  const postSpacing = 4;
  const mid = length / 2;
  const parts: GreyboxPart[] = [
    box([world.trackWidth, 0.2, length], 0, -0.2, mid, M.Track),
    box([sideWidth, 0.2, length], -(halfTrack + curbWidth + sideWidth / 2), -0.24, mid, M.TrackSide),
    box([sideWidth, 0.2, length], halfTrack + curbWidth + sideWidth / 2, -0.24, mid, M.TrackSide),
    box([curbWidth, 0.15, length], -(halfTrack + curbWidth / 2), 0, mid, M.Curb),
    box([curbWidth, 0.15, length], halfTrack + curbWidth / 2, 0, mid, M.Curb),
    box([0.06, fenceHeight - 0.2, length], -fenceX, 0.15, mid, M.Fence),
    box([0.06, fenceHeight - 0.2, length], fenceX, 0.15, mid, M.Fence),
  ];
  for (let lane = 1; lane < world.laneCount; lane++) {
    const x = -halfTrack + lane * world.laneWidth;
    parts.push(box([0.08, 0.01, length], x, 0.002, mid, M.LaneLine));
  }
  for (let z = 0; z < length - 1e-6; z += postSpacing) {
    parts.push(box([0.12, fenceHeight, 0.12], -fenceX, 0, z, M.FencePost));
    parts.push(box([0.12, fenceHeight, 0.12], fenceX, 0, z, M.FencePost));
  }
  return parts;
}

/** Simple blocky worker – origin at the feet, facing +Z. */
export function workerGreybox(vest: GreyboxMaterialId, helmet: GreyboxMaterialId): GreyboxPart[] {
  return [
    box([0.22, 0.85, 0.26], -0.13, 0, 0, M.WorkerPants),
    box([0.22, 0.85, 0.26], 0.13, 0, 0, M.WorkerPants),
    box([0.56, 0.6, 0.32], 0, 0.85, 0, M.WorkerShirt),
    box([0.6, 0.44, 0.36], 0, 0.98, 0, vest),
    box([0.15, 0.58, 0.17], -0.37, 0.85, 0, M.WorkerShirt),
    box([0.15, 0.58, 0.17], 0.37, 0.85, 0, M.WorkerShirt),
    sphere(0.3, 0, 1.6, 0, M.Skin),
    cylinder(0.36, 0.14, 0, 1.68, 0, helmet),
    box([0.42, 0.03, 0.46], 0, 1.68, 0.03, helmet),
  ];
}

export interface JunctionOpenings {
  readonly left: boolean;
  readonly right: boolean;
  readonly straight: boolean;
}

/** Ground chevron (two arms) pointing in `yawDegrees` (0 = +Z, −90 = left, +90 = right). */
export function groundChevron(tipX: number, tipZ: number, yawDegrees: number): GreyboxPart[] {
  const armLength = 1.4;
  return [yawDegrees + 135, yawDegrees - 135].map((armYaw) => {
    const rad = (armYaw * Math.PI) / 180;
    const cx = tipX + Math.sin(rad) * (armLength / 2);
    const cz = tipZ + Math.cos(rad) * (armLength / 2);
    return box([0.3, 0.01, armLength], cx, 0.003, cz, M.LaneLine, [0, armYaw, 0]);
  });
}

/**
 * Greybox of a junction module (see junctions.ts for the layout). Side ground
 * sits a few centimeters lower than on straight modules, so areas shared with
 * the exit modules never z-fight.
 */
export function junctionGreybox(world: WorldConfig, length: number, open: JunctionOpenings): GreyboxPart[] {
  const half = world.trackWidth / 2;
  const approachEnd = length - 2 * half;
  const pivotZ = length - half;
  const curbWidth = 0.3;
  const fenceX = 4.2;
  const sideWidth = 12;
  const lowSideY = -0.3;
  const farDepth = 12;
  const postSpacing = 4;

  const parts: GreyboxPart[] = [box([world.trackWidth, 0.2, length], 0, -0.2, length / 2, M.Track)];
  for (let lane = 1; lane < world.laneCount; lane++) {
    parts.push(box([0.08, 0.01, approachEnd], -half + lane * world.laneWidth, 0.002, approachEnd / 2, M.LaneLine));
  }
  // Pre-announcement on the ground: one chevron per possible direction.
  if (open.left) parts.push(...groundChevron(-1.3, 8.5, -90));
  if (open.right) parts.push(...groundChevron(1.3, 8.5, 90));
  if (open.straight) parts.push(...groundChevron(0, 10.5, 0));

  for (const side of [-1, 1] as const) {
    const closed = side < 0 ? !open.left : !open.right;
    const curbLength = closed ? length : approachEnd - curbWidth;
    const fenceLength = closed ? length + (open.straight ? 0 : 0.9) : approachEnd - (fenceX - half);
    const groundLength = closed ? length + (open.straight ? 0 : 0.9) : approachEnd - curbWidth;
    parts.push(box([curbWidth, 0.15, curbLength], side * (half + curbWidth / 2), 0, curbLength / 2, M.Curb));
    parts.push(box([0.06, 1.8, fenceLength], side * fenceX, 0.15, fenceLength / 2, M.Fence));
    for (let z = 0; z < fenceLength - 1e-6; z += postSpacing) parts.push(box([0.12, 2, 0.12], side * fenceX, 0, z, M.FencePost));
    parts.push(box([sideWidth, 0.2, groundLength], side * (half + curbWidth + sideWidth / 2), lowSideY, groundLength / 2, M.TrackSide));
    if (closed) {
      // Concrete barrier along the closed side of the square.
      parts.push(box([0.5, 1, 2 * half], side * 3.9, 0, pivotZ, M.Barrier));
    }
  }

  if (!open.straight) {
    const farWidth = 2 * fenceX + 0.12;
    parts.push(box([farWidth, 1, 0.5], 0, 0, length + 0.35, M.Barrier));
    parts.push(box([world.trackWidth, 1, 0.08], 0, 1.05, length + 0.45, M.Chevron));
    // Posts carrying the ad slot AD_SCAFFOLD_HIGH (surface is created by the ad system).
    parts.push(box([0.15, 4.5, 0.15], -3.1, 0, length + 0.75, M.Steel));
    parts.push(box([0.15, 4.5, 0.15], 3.1, 0, length + 0.75, M.Steel));
    parts.push(box([farWidth + 2 * sideWidth, 0.2, farDepth], 0, lowSideY, length + 0.9 + farDepth / 2, M.TrackSide));
  }
  return parts;
}

/**
 * Greybox of SEG_COLLAPSE_40M: straight track, normal fence on the right, the
 * building plot on the left with what remains standing after the collapse
 * (columns, back wall, broken floor slabs, rubble). The falling pieces are the
 * debris obstacles themselves (animated by CollapsePresenter).
 */
export function collapseSegmentGreybox(world: WorldConfig, length: number): GreyboxPart[] {
  const half = world.trackWidth / 2;
  const mid = length / 2;
  const parts: GreyboxPart[] = [
    box([world.trackWidth, 0.2, length], 0, -0.2, mid, M.Track),
    box([0.3, 0.15, length], -(half + 0.15), 0, mid, M.Curb),
    box([0.3, 0.15, length], half + 0.15, 0, mid, M.Curb),
    // right side: fence + ground like a straight module
    box([0.06, 1.8, length], 4.2, 0.15, mid, M.Fence),
    box([12, 0.2, length], half + 0.3 + 6, -0.24, mid, M.TrackSide),
    // left side: building plot
    box([14, 0.2, length], -(half + 0.3 + 7), -0.24, mid, M.TrackSide),
    // remaining structure of the building
    box([0.3, 7.6, 25], -14.6, 0, 23.5, M.Concrete),
    box([5, 0.3, 7], -12, 3.6, 33, M.Concrete),
    box([5, 0.3, 5], -12, 7.2, 12.5, M.Concrete),
    // rubble that stays on the plot
    box([1.6, 0.6, 1.2], -5.6, 0, 18, M.Concrete, [0, 25, 8]),
    box([1.2, 0.5, 1], -6.6, 0, 29, M.Concrete, [0, -30, 0]),
    box([2.2, 0.4, 1.4], -7.4, 0, 9, M.Concrete, [0, 15, -6]),
  ];
  for (const [x, z] of [[-9.6, 11], [-9.6, 36], [-14.4, 11], [-14.4, 23.5], [-14.4, 36]] as const) {
    parts.push(box([0.5, 11, 0.5], x, 0, z, M.Concrete));
  }
  for (let z = 0; z < length - 1e-6; z += 4) parts.push(box([0.12, 2, 0.12], 4.2, 0, z, M.FencePost));
  for (let lane = 1; lane < world.laneCount; lane++) {
    parts.push(box([0.08, 0.01, length], -half + lane * world.laneWidth, 0.002, mid, M.LaneLine));
  }
  return parts;
}
